'use client';

import { useState } from 'react';
import clsx from 'clsx';

import styles from './Rating.module.scss';
import StarDefaultIcon from '@/ui-kit/Icons/StarDefault';
import StarColoredRedIcon from '@/ui-kit/Icons/StarColoredRed';
import StarColoredOrangeIcon from '@/ui-kit/Icons/StarColoredOrange';
import StarColoredYellowIcon from '@/ui-kit/Icons/StarColoredYellow';
import StarColoredGreenLightIcon from '@/ui-kit/Icons/StarColoredGreenLight';
import StarColoredGreenIcon from '@/ui-kit/Icons/StarColoredGreen';

interface RatingProps {
  rating: number;
  name?: string;
  required?: boolean;
  variant?: 'default' | 'small' | 'large';
  coloredStars?: boolean;
}

const Rating = ({
  rating,
  name,
  required,
  variant = 'default',
  coloredStars = true,
}: RatingProps) => {
  const [ratingValue, setRatingValue] = useState(rating);
  const roundedRating = Math.round(rating);

  const getStarIconByScore = (score: number) => {
    if (score <= 1) return StarColoredRedIcon;
    if (score <= 2) return StarColoredOrangeIcon;
    if (score <= 3) return StarColoredYellowIcon;
    if (score <= 4) return StarColoredGreenLightIcon;
    return StarColoredGreenIcon;
  };

  const currentScore = name ? ratingValue : roundedRating;
  const ColoredIcon = getStarIconByScore(currentScore);

  return (
    <div className={clsx(styles.rating, styles[variant])}>
      {Array.from({ length: 5 }, (_, i) => {
        const value = i + 1;
        const isActive = value <= roundedRating;

        return (
          <label key={value} className={styles.label}>
            {name && (
              <input
                type="radio"
                name={name}
                value={value}
                id={`${name}-${value}`}
                className={`${styles.input} ${ratingValue >= value ? styles.active : ''}`}
                onChange={() => setRatingValue(value)}
                required={required}
                defaultChecked={value === roundedRating}
                aria-label={`Rate ${value} out of 5 stars`}
              />
            )}
            <span className={styles.star}>
              {isActive && coloredStars ? (
                <ColoredIcon aria-hidden="true" />
              ) : (
                <StarDefaultIcon aria-hidden="true" />
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
};

export default Rating;
